import {
  ArrowTopRightOnSquareIcon,
  InformationCircleIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { Control, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Alert, Button, Loading } from '@/components/ui/daisy';
import {
  ORDENABLE_PAGE_SIZE,
  REQUEST_STALE_TIME_1_DAY_IN_MS,
} from '@/constants';
import useListService from '@/data/hook/useListService';
import useToggle from '@/data/hook/useToggle';
import { coursePathsQueryKeys } from '@/data/services/querykeys';
import usePedagogicalContentTo from '@/data/hook/evaluation/usePedagogicalContentTo';
import { EvaluationBlockInlineFormFields } from './EvaluationBlockInlineForm';
import EvaluationBlockSlavesList from './EvaluationBlockSlavesList';
import EvaluationTemplateApplyModal from './EvaluationTemplateApplyModal';

type CoursePathsFieldProps = {
  control: Control<EvaluationBlockInlineFormFields>;
  courseLineId: number;
};

export default function CoursePathsField({
  control,
  courseLineId,
}: CoursePathsFieldProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });

  const pedagogicalContentTo = usePedagogicalContentTo(courseLineId);

  const { results: coursePaths, isLoading } = useListService({
    ...coursePathsQueryKeys.list({
      courseLineId: [courseLineId],
      pageSize: ORDENABLE_PAGE_SIZE,
    }),
    staleTime: REQUEST_STALE_TIME_1_DAY_IN_MS,
  });

  const { fields, remove, replace } = useFieldArray({
    control,
    name: 'coursePaths',
    keyName: 'fieldId',
  });

  const { isOpen, close, open } = useToggle(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-1.5">
        <Loading variant="spinner" size="sm" color="primary" />
      </div>
    );
  }

  if (!coursePaths.length) {
    return (
      <div className="flex flex-col gap-4">
        <Alert variant="soft">
          <InformationCircleIcon className="w-6 h-6 shrink-0 text-info" />
          <div className="flex flex-col">
            <span className="font-bold text-sm">
              {t('pedagogicalContentMissingTitle')}
            </span>
            <span className="text-xs">
              {t('pedagogicalContentMissingDescription')}
            </span>
          </div>
        </Alert>
        {pedagogicalContentTo && (
          <div className="flex justify-end">
            <Link to={pedagogicalContentTo.to}>
              <Button size="sm">
                {t('goToPedagogicalContent')}
                <ArrowTopRightOnSquareIcon className="w-3 h-3" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <ConditionalRenderer condition={fields.length > 0}>
        <EvaluationBlockSlavesList
          courseLineId={courseLineId}
          appliedCoursePaths={fields}
          onRemove={remove}
        />
      </ConditionalRenderer>
      <Button
        type="button"
        size="sm"
        variant="soft"
        color="primary"
        className="self-start"
        onClick={open}
      >
        <PlusIcon className="w-3 h-3" />
        {t('addAssessmentItem')}
      </Button>
      <EvaluationTemplateApplyModal
        courseLineId={courseLineId}
        appliedCoursePaths={fields}
        onApply={replace}
        isOpen={isOpen}
        onClose={close}
      />
    </div>
  );
}
